import dayjs from "dayjs";
import { uniqBy } from "lodash";
import React, { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import useScrollToMessage from "../../../hooks/useScrollToMessage";
import { SetContentBy } from "./engagementHelpers";
import Messages from "./messages";

// Animation duration constant (in milliseconds)
const MESSAGE_HIGHLIGHT_DURATION = 4000;

const MessagesGroup = ({
  date,
  dateMessages,
  userIdMap,
  handleOpportunityId,
  handleRetry,
  targetMessageId,
  ...props
}) => {
  const { selectedLead } = useSelector(state => state.lead);

  // Track which messageIds have already been animated (persists across re-renders)
  const animatedMessagesRef = useRef(new Set());

  // Determine if we should trigger auto-scroll
  // Only scroll if we have a messageId AND messages to search through AND not already animated
  const hasNotBeenAnimated = !animatedMessagesRef.current.has(targetMessageId);
  // Only the date group that actually CONTAINS the target message should run
  // the scroll hook. Without this every group runs it in parallel (redundant
  // polling, N× image listeners, N× competing scrollIntoView).
  const containsTarget =
    targetMessageId && (dateMessages || []).some(m => +m?.id === +targetMessageId);
  const shouldScrollToMessage =
    targetMessageId &&
    containsTarget &&
    dateMessages &&
    dateMessages.length > 0 &&
    hasNotBeenAnimated;

  // Auto-scroll to specific message after all messages in this group are rendered
  useScrollToMessage({
    messageId: shouldScrollToMessage ? targetMessageId : null,
    messages: dateMessages,
    behavior: "smooth",
    block: "center",
    onScrollComplete: element => {
      // Mark this message as animated so it won't animate again
      animatedMessagesRef.current.add(targetMessageId);

      // Set CSS variable for animation duration
      element.style.setProperty("--message-highlight-duration", `${MESSAGE_HIGHLIGHT_DURATION}ms`);

      // Add highlight animation
      element.classList.add("message-highlight");

      // Remove the class after animation completes
      setTimeout(() => {
        element.classList.remove("message-highlight");
        element.style.removeProperty("--message-highlight-duration");
      }, MESSAGE_HIGHLIGHT_DURATION);
    },
  });

  // Dedup + sort only when the group's messages actually change, instead of on
  // every parent re-render (typing, socket events, etc.). Cheap on its own, but
  // it runs for every date-group so it adds up as history grows.
  const visibleMessages = useMemo(() => {
    const deduped = uniqBy(
      (dateMessages || []).map(item => ({ ...item, id: +item.id })),
      "id"
    );
    return deduped.sort((a, b) => {
      const dateA = dayjs(a.dateReceived || a.dateSent);
      const dateB = dayjs(b.dateReceived || b.dateSent);
      // Ascending so the latest message is at the bottom.
      return dateA.isBefore(dateB) ? -1 : 1;
    });
  }, [dateMessages]);

  return (
    <>
      {visibleMessages?.length > 0 && (
        <div className="flex-row-centered text-center items-center my-3 md:my-5 mx-0">
          <div className="flex-1 h-[1px] bg-grey-200" />
          <div className="px-5 text-xs font-[400] text-grey-600">
            {dayjs(date, "MM/DD/YYYY").format("MMM DD, YYYY")}
          </div>
          <div className="flex-1 h-[1px] bg-grey-200" />
        </div>
      )}

      <div className="flex-col-centered gap-3 md:gap-4">
        {visibleMessages.map((item, index) => {
          const messageProps = {
            ...props,
            userIdMap,
            handleRetry,
            selectedLead,
            SetContentBy,
            handleOpportunityId,
          };
          return <Messages key={item.id} c={item} messageIndex={index} {...messageProps} />;
        })}
      </div>
    </>
  );
};

export default MessagesGroup;
